import { useRef, useCallback } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { findPath } from "./pathfinding";

const MOVE_SPEED = 4;
const WAYPOINT_THRESHOLD = 0.3; // how close to a waypoint before advancing to next

const _direction = new THREE.Vector3();

export default function useAvatarMovement() {
  const position = useRef(new THREE.Vector3(-5, 0, -5));
  const targetPosition = useRef(new THREE.Vector3(-5, 0, -5));
  const isMoving = useRef(false);
  const waypoints = useRef<THREE.Vector3[]>([]);

  const setTarget = useCallback((point: THREE.Vector3) => {
    const target = new THREE.Vector3(point.x, 0, point.z);
    targetPosition.current.copy(target);

    // Compute A* path
    const path = findPath(position.current, target);
    waypoints.current = path;
    isMoving.current = path.length > 0;
  }, []);

  useFrame((_state, delta) => {
    if (!isMoving.current || waypoints.current.length === 0) {
      isMoving.current = false;
      return;
    }

    const current = position.current;
    const nextWaypoint = waypoints.current[0];
    const distToWaypoint = current.distanceTo(nextWaypoint);

    // Reached current waypoint — advance to next
    if (distToWaypoint < WAYPOINT_THRESHOLD) {
      current.copy(nextWaypoint);
      waypoints.current.shift();

      if (waypoints.current.length === 0) {
        isMoving.current = false;
        return;
      }
    }

    // Move toward current waypoint
    const wp = waypoints.current[0];
    _direction.subVectors(wp, current);
    _direction.y = 0;
    const dist = _direction.length();
    if (dist > 0.001) {
      _direction.normalize();
      const step = Math.min(MOVE_SPEED * delta, dist);
      current.addScaledVector(_direction, step);
      current.y = 0;
    }
  });

  return { position, isMoving, setTarget, waypoints };
}
